import React, { useMemo } from 'react';
import { Tooltip } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { ProposalData } from '../types';
import { formatPrice, getTierRangeLabel } from '../utils';
import { GroupUserIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';

interface PriceCellProps {
  proposal: ProposalData;
}

const PriceCell: React.FC<PriceCellProps> = ({ proposal }) => {
  const { t } = useTranslations();

  const isSubscription = proposal.offerType === 'subscription';

  const { displayPrice, tooltipContent } = useMemo(() => {
    const tiers = proposal.price.tiers || [];
    const firstTierPrice = tiers[0]?.price ?? proposal.price.pricePerSubscriber;

    // У подписки одна цена без ступеней — вместо разбивки по тирам
    // показываем простую подсказку.
    if (isSubscription) {
      return {
        displayPrice: firstTierPrice,
        tooltipContent: (
          <Typography variant="body2">{t('proposals.price.subscriptionTooltipTitle')}</Typography>
        ),
      };
    }

    const content = tiers.length ? (
        <div className="flex min-w-[6.25rem] max-w-[10.5rem] flex-col gap-2">
        <Typography variant="body2">{t('proposals.price.tooltipTitle')}</Typography>
        <div className="flex flex-col gap-1">
          {tiers.map((tier, index) => {
            const rangeLabel = getTierRangeLabel(tiers, index);
            return (
              <div
                key={`${tier.from}-${tier.to ?? 'plus'}`}
                className="flex items-center justify-between gap-3"
              >
                <Typography variant="body3" className="text-auth-ink">
                  {rangeLabel}
                </Typography>
                <Typography variant="body3" className="text-auth-ink font-semibold">
                  {formatPrice(tier.price)}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

    return {
      displayPrice: firstTierPrice,
      tooltipContent: content,
    };
  }, [proposal.price.pricePerSubscriber, proposal.price.tiers, isSubscription, t]);

  return (
    <div className="flex flex-col gap-1.5 max-md:whitespace-nowrap">
      <ResponsiveTooltip
        placement="top"
        content={tooltipContent || undefined}
        isDisabled={!tooltipContent}
        className="rounded-2.5 px-3 py-2.5"
      >
        <div className={`flex items-baseline ${tooltipContent ? 'cursor-pointer' : ''}`}>
          <span className="text-sm font-semibold text-auth-ink">{formatPrice(displayPrice)}</span>
          <span className="text-xs text-auth-muted">
            &nbsp;
            {isSubscription ? t('proposals.price.perMonth') : t('proposals.price.perFan')}
          </span>
        </div>
      </ResponsiveTooltip>
      <ResponsiveTooltip content={t('proposals.price.trafficVolumeTooltip')} placement="top">
        <div className="flex items-center gap-1 cursor-pointer">
          <GroupUserIc />
          {/* У подписки нет диапазона "всего подписчиков" — показываем объём в месяц. */}
          <span className="text-xs text-auth-muted">
            {isSubscription ? (
              <>
                ~{proposal.campaign.subsPerDay} {t('proposals.price.fansPerMonth')}
              </>
            ) : (
              <>
                {proposal.campaign.targetSubscribers} - {proposal.campaign.targetSubscribersMax}
              </>
            )}
          </span>
        </div>
      </ResponsiveTooltip>
    </div>
  );
};

export default PriceCell;
